import { useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../components/Generic/Toast/NotificationToast";
import { useAppState } from "../context/StateContext";
import { AutomaticSequencing } from "../models/automatic.sequencing";
import AutomaticSequencingService from "../services/automatic.sequencing.service";
import { AutomaticSequenceOptions } from "../util/enums";

interface IncreaseAutomaticSequenceProps {
  id?: number;
  sequence: AutomaticSequencing;
  value: string;
}

export const MAX_AUTOMATIC_SEQUENCE_VALUE = 2147483647;

export const isAutomaticSequenceAvailable = (
  sequence?: AutomaticSequencing
): sequence is AutomaticSequencing => {
  const nextValue = Number(sequence?.nextValue);

  return Boolean(
    sequence?.id &&
      Number.isFinite(nextValue) &&
      nextValue > 0 &&
      nextValue < MAX_AUTOMATIC_SEQUENCE_VALUE
  );
};

export const getAutomaticSequenceReference = (sequence?: AutomaticSequencing): string => {
  if (!isAutomaticSequenceAvailable(sequence)) return "";

  return `${sequence.prefix || ""}${sequence.nextValue}`;
};

export const isAutomaticSequenceReference = (
  sequence: AutomaticSequencing | undefined,
  value?: string
): boolean => {
  if (!value) return false;

  return value.trim() === getAutomaticSequenceReference(sequence);
};

export const getAutomaticSequenceNextValue = (
  sequence: AutomaticSequencing | undefined,
  value?: string
): number | undefined => {
  if (!isAutomaticSequenceReference(sequence, value)) return undefined;

  return Number(sequence?.nextValue);
};

const useActionAutomaticSequence = () => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const [sequences, setSequences] = useState<AutomaticSequencing[]>([]);
  const [currentSequences, setCurrentSequences] = useState<AutomaticSequencing[]>([]);

  const checkForAutomaticSequence = (value: string): boolean => {
    return !!value.match(/\d+/g);
  };

  const getAllSequences = () => {
    return useQuery(
      ["automatic.sequence.list"],
      () =>
        AutomaticSequencingService.getAll({
          companyId: currentUser?.companyId,
        }),
      {
        enabled: !!currentUser?.companyId,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          if (response && response.data) {
            setCurrentSequences(response.data);
            setSequences(response.data);
          }
        },
      }
    );
  };

  const getAutomaticSequenceByType = (
    type: AutomaticSequenceOptions,
    enabled: boolean,
    setSequence: (sequence: AutomaticSequencing) => void
  ) => {
    return useQuery(
      ["automatic.sequence.available", type],
      () =>
        AutomaticSequencingService.getAvailableSequence(Number(currentUser?.companyId), { type }),
      {
        enabled: !!currentUser?.companyId && !!type && enabled,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          if (!response?.data) return;

          if (!isAutomaticSequenceAvailable(response.data)) {
            NotificationToast.warning(
              translate("automaticSequenceUnavailableEnterReferenceManually"),
              {
                id: `automatic-sequence-unavailable-${type}`,
              }
            );
            return;
          }

          setSequence(response.data);
        },
      }
    );
  };

  const createAutomaticSequence = (refetch?: () => void) => {
    return useMutation(
      ["create.sequence"],
      (sequence: AutomaticSequencing) => AutomaticSequencingService.create(sequence),
      {
        onSuccess: () => {
          NotificationToast.success(translate("operationPerformedSuccessfully"), {
            id: "request.notification.automation.create",
          });
          if (refetch) refetch();
        },
      }
    );
  };

  const updateAutomaticSequence = (refetch?: () => void) => {
    return useMutation(
      ["update.sequence"],
      ({ id, sequence }: { id: number; sequence: AutomaticSequencing }) =>
        AutomaticSequencingService.update(id, sequence),
      {
        onSuccess: () => {
          NotificationToast.success(translate("operationPerformedSuccessfully"), {
            id: "request.notification.automation",
          });
          if (refetch) refetch();
        },
      }
    );
  };

  const { mutate: increaseAutomaticSequence } = useMutation(
    ["update.sequence"],
    ({ sequence, value }: IncreaseAutomaticSequenceProps) => {
      const nextValue = getAutomaticSequenceNextValue(sequence, value);

      if (!nextValue) return Promise.resolve({ data: false });

      return AutomaticSequencingService.increaseSequence({
        ...sequence,
        nextValue,
      });
    }
  );

  return {
    sequences,
    setSequences,
    currentSequences,
    getAllSequences,
    createAutomaticSequence,
    updateAutomaticSequence,
    checkForAutomaticSequence,
    isAutomaticSequenceReference,
    increaseAutomaticSequence,
    getAutomaticSequenceByType,
  };
};

export default useActionAutomaticSequence;
